import React, { useState, useMemo, useCallback } from 'react'
import moment from 'moment'

import { CruiseContentSections, CRUISE_CONTENT_SECTION_LABELS } from 'constants/cruise'

import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import Sheet from 'components/utils/Sheet'
import { groupBy, sortBy } from 'lib/array/arrayUtils'

import CruiseDepartureFilters from './CruiseDepartureFilters'
import NeedHelp from '../NeedHelp'
import CruiseDepartureTile from './CruiseDepartureTile/CruiseDepartureTile'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

export type DeparturesByDate = {
  [date: string]: Array<App.CruiseDepartureDetails>
}

interface Props {
  offer: App.CruiseOffer
  departuresByDate: DeparturesByDate
  hasValidDepartures: boolean
  onHandleSelectDeparture: (departure: App.CruiseDepartureDetails) => void
}

function CruiseOfferPageDepartures({
  offer,
  departuresByDate,
  hasValidDepartures,
  onHandleSelectDeparture,
}: Props) {
  const { lowestDeparturePricesByCategory } = offer
  const [selectedYear, setSelectedYear] = useState<number | undefined>()
  const [sort, setSort] = useState<string | undefined>()

  const getPricesFromDeparture = useCallback((departure: App.CruiseDepartureDetails) => {
    return Object.values(lowestDeparturePricesByCategory[departure.id] || {})
      .map((price) => price.total)
  }, [lowestDeparturePricesByCategory])

  const yearOptions = useMemo(
    () => {
      const years = Object.keys(departuresByDate).map(date => Number(date.split('-')[0]))
      const uniqueYears = Array.from(new Set(years))

      return sortBy(uniqueYears, year => year, 'asc')
    },
    [departuresByDate],
  )

  const departureList = useMemo(() => (
    Object.values(departuresByDate)
      .flat()
      .map((departure) => {
        const prices = getPricesFromDeparture(departure)
        const lowestPrice = Math.min(...prices)
        const highestPrice = Math.max(...prices)

        const dateMoment = moment(departure.departureDate)

        const sortingData = {
          lowestPrice,
          highestPrice,
          dateUnix: dateMoment.unix(),
          year: dateMoment.year(),
        }

        return {
          dateLabel: dateMoment.format('MMMM YYYY'),
          departure,
          sortingData,
        }
      })
  ), [departuresByDate, getPricesFromDeparture])

  const filteredAndSortedDepartureList = useMemo(() => (
    departureList
      .filter(({ sortingData }) => !selectedYear || sortingData.year === selectedYear)
      .sort((a, b) => {
        if (sort === 'price-asc') {
          return a.sortingData.lowestPrice - b.sortingData.lowestPrice
        }

        if (sort === 'price-desc') {
          return b.sortingData.highestPrice - a.sortingData.highestPrice
        }

        return a.sortingData.dateUnix - b.sortingData.dateUnix
      })
  ), [departureList, selectedYear, sort])

  const departuresByMonth = useMemo(
    () => Array.from(groupBy(filteredAndSortedDepartureList, ({ dateLabel }) => dateLabel).entries()),
    [filteredAndSortedDepartureList],
  )

  const hasMultipleDepartures = departureList.length > 1

  return <LayoutContainer>
    <ScrollAnchor id="departures-anchor" />

    <Sheet padding="32 0">
      <Group direction="vertical" gap={40}>
        <Heading variant="heading2" align="center" className="section-title">
          {CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.DEPARTURES]}
        </Heading>

        {hasMultipleDepartures && <CruiseDepartureFilters
          sort={sort}
          yearOptions={yearOptions}
          selectedYear={selectedYear}
          setSort={setSort}
          setSelectedYear={setSelectedYear}
        />}

        <Group direction="vertical" gap={40}>
          {departuresByMonth.map(([dateLabel, departures]) => (
            <VerticalSpacer gap={28} key={dateLabel}>
              <VerticalSpacer gap={16}>
                <BodyText variant="large" weight="bold" align="start">{dateLabel}</BodyText>
                <Divider kind="secondary" />
              </VerticalSpacer>

              {departures.map(({ departure }) => (
                <CruiseDepartureTile
                  offer={offer}
                  key={departure.id}
                  departure={departure}
                  onHandleDepartureModal={onHandleSelectDeparture}
                />
              ))}
            </VerticalSpacer>
          ))}

          {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
          {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
          {hasValidDepartures && <NeedHelp />}
        </Group>
      </Group>
    </Sheet>
  </LayoutContainer>
}

export default CruiseOfferPageDepartures
